import * as moment from 'moment';
import { Component } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AuthService } from '@infra/auth/auth.service';
import { MultaModel } from '@data/multas/models';
import { MultaRepository } from '@data/multas/multa.repository';
import { BaseListagemComponent } from '@common/modules/base/base-listagem.component';
import { AddCondutorContratoComponent } from '@modules/multas/components/add-condutor-contrato/add-condutor-contrato.component';
import { AddCondutorComponent } from '@modules/multas/components/add-condutor/add-condutor.component';
import { DetalhesMultaComponent } from '@modules/multas/components/detalhes-multa/detalhes-multa.component';
import { MultasController } from '@modules/multas/controllers';
import { ExcelService } from '@shared/utils/excel.util';
import { Coluna } from '@components/table/table.component';
import { Acao } from '@components/filtros/filtros.component';
import { Campo } from '@components/form/form.component';
import { currencyBRL, dateFormat, dateHoursFormat } from '@app/shared/formatters';
import { modal } from '@app/shared/alerts/modal/modal.alert';

@Component({
  selector: 'app-multas',
  templateUrl: './multas.component.html',
  styleUrls: ['./multas.component.scss']
})
export class MultasComponent extends BaseListagemComponent<MultaModel>{
  filtros: Campo[] = [
    {
      name: 'contrato.grupoContrato.numeroGrupoContrato',
      label: 'Contrato Master',
      type: 'text',
      icon: 'insert_drive_file',
      width: '25%',
      mask: '',
      validators: [],
    },
    {
      name: 'notificacao',
      label: 'Tipo',
      type: 'select',
      icon: 'check_circle_outline',
      width: '25%',
      selectOptions: {
        items: [
          { label: 'Multa', value: 'N' },
          { label: 'Notificação', value: 'S' },
        ]
      },
      validators: [],
    },
    {
      name: 'veiculo.placa',
      label: 'Placa',
      type: 'text',
      icon: 'directions_car',
      width: '25%',
      mask: '',
      validators: [],
    },
    {
      name: 'dataHora',
      label: 'Período abertura',
      type: 'date-range',
      width: '25%',
      dateRangeOptions: {
        icon: 'prefix',
      },
      validators: [],
    },
  ];

  protected acoes: Acao[] = [
    {
      icon: 'save_alt',
      tooltip: 'Baixar Excel',
      action: this.baixarExcel.bind(this),
    },
  ];

  public colunas: Coluna[] = [
    { name: 'numeroContrato', label: 'Contrato Master' },
    { name: 'placa', label: 'Placa' },
    { name: 'modelo', label: 'Modelo' },
    { name: 'data', label: 'Data/Hora', format: dateHoursFormat },
    { name: 'codigoInfracao', label: 'Código Infração' },
    { name: 'descricaoInfracao', label: 'Descrição Infração', truncate: 30 },
    { name: 'localInfracao', label: 'Local Infração' },
    { name: 'numeroAIT', label: 'Número AIT' },
    { name: 'dataVencimento', label: 'Data Venc.', format: dateFormat },
    { name: 'dataIdentificacaoCondutor', label: 'Data Identificação Condutor', format: dateFormat },
    { name: 'condutor', label: 'Condutor' },
    { name: 'valor', label: 'Valor', format: currencyBRL },
    {
      name: 'acoes',
      label: 'Ações',
      acoes: [
        {
          label: 'Anexos',
          icon: 'attach_file',
          action: this.anexarArquivos.bind(this),
          disabled: !this.authService.hasPermission('multas', 'EDITAR')
        },
        {
          label: 'Inserir novo condutor',
          icon: 'fiber_new',
          action: this.newDriver.bind(this),
          disabled: !this.authService.hasPermission('multas', 'EDITAR')
        },
        {
          label: 'Inserir condutor do contrato',
          icon: 'person_add',
          action: this.insertDriverFromContract.bind(this),
          disabled: !this.authService.hasPermission('multas', 'EDITAR')
        },
        {
          label: 'Recusar Indicação do Condutor',
          icon: 'block',
          action: this.refuseDriverIndication.bind(this),
          disabled: !this.authService.hasPermission('multas', 'EDITAR')
        },
      ],
    },
  ];

  constructor(
    public multasController: MultasController,
    private dialog: MatDialog,
    public excelService: ExcelService,
    private authService: AuthService,
    private multaRepository: MultaRepository,
  ) {
    super('Multas - Portal do Cliente', multasController);
  }

  public async baixarExcel() {
    const lastItems = this.items;
    this.paginator.pageSize = 9999;
    await this.filtrar();

    let colunasMapped = this.colunas.map((coluna: any) => coluna.label);
    colunasMapped = colunasMapped.filter((coluna) => coluna !== 'Ações')

    // Os valores de cada objeto devem estar ordenados com as colunas
    const sortedArray = this.items.map((obj: any) => {
      return {
        numeroContrato: obj.numeroContrato,
        placa: obj.placa,
        modelo: obj.modelo,
        data: obj.data,
        hora: obj.hora,
        codigoInfracao: obj.codigoInfracao,
        descricaoInfracao: obj.descricaoInfracao,
        localInfracao: obj.localInfracao ? obj.localInfracao.replaceAll('\n', ' ') : '',
        numeroAIT: obj.numeroAIT,
        dataVencimento: obj.dataVencimento,
        dataIdentificacaoCondutor: obj.dataIdentificacaoCondutor,
        condutor: obj.condutor,
        valor: obj.valor,
      }
    });
    this.excelService.exportToFile(colunasMapped, sortedArray, 'Detalhamento das multas');

    this.paginator.pageSize = 10;
    this.items = lastItems;
  }

  public anexarArquivos(multa: any) {
    this.dialog.open(DetalhesMultaComponent, {
      width: '800px',
      height: '600px',
      data: { codigoMulta: multa.codigoMulta },
    });
  }

  insertDriverFromContract(multaItem: any): void {
    this.dialog.open(AddCondutorContratoComponent, {
      height: '180px',
      width: '600px',
      data: multaItem,
      disableClose: true,
    }).afterClosed().subscribe(() => this.filtrar()); 
  }

  newDriver(multaItem: any): void {
    this.dialog.open(AddCondutorComponent, {
      maxHeight: screen.height / 2 + 200,
      width: '1200px',
      data: multaItem,
      disableClose: true,
    }).afterClosed().subscribe(() => this.filtrar());
  }

  refuseDriverIndication(multaItem: any): void {
    modal.fire({
      title: 'Atenção!',
      html: 'A opção por recusar indicação do condutor resultará no faturamento da multa + o valor correspondente ao agravo conforme a legislação vigente. <br><br><b>Essa opção não pode ser desfeita. Continuar?</b>',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sim, recusar indicação',
      cancelButtonText: 'Não, cancelar',
      reverseButtons: true
    }).then((result) => {
      if (result.isConfirmed) {
        this.multaRepository.recusarIndicacaoCondutor(multaItem.codigoMulta).subscribe({
          next: () => {
            this.filtrar();

            modal.fire({
              icon: 'success',
              title: 'Sucesso!',
              text: 'Indicação de condutor recusada, a cobrança correspondente será gerada.',
              confirmButtonText: 'Continuar',
            });
          },
          error: (error) => {
            modal.fire({
              icon: error.icon || 'error',
              title: error.title || 'Erro!',
              html: error.message || 'Ocorreu um erro ao recusar a indicação.',
            });
          }
        });
      }
    });
  }
}
